import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { buildInitialWhereFromFilters, FilterBar } from '@client/components/filters';
import { TableViewsBar, useEntityBuiltInViews, useTableViews } from '@client/components/table-views';
import { usePermissions } from '@client/hooks/auth/usePermissions';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { type RouterInputs, trpc } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { FilterOperatorSchema } from '@shared/schemas/filter';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { UserCheck } from 'lucide-react';
import { useMemo, useState } from 'react';
import { z } from 'zod';
import { userAttestationColumns } from './columns';
import { useUserAttestationFilterConfig } from './useUserAttestationFilterConfig';

const UserAttestationSearchSchema = z.object({
    'user-attestation-filters': z
        .array(
            z.object({
                id: z.string(),
                filterId: z.string(),
                operator: FilterOperatorSchema,
                value: z.any(),
            }),
        )
        .optional()
        .catch(undefined),
    view: z.string().optional().catch(undefined),
});

export const Route = createFileRoute('/console/$orgId/attestations/user-attestations')({
    staticData: {
        metadata: {
            title: 'User attestations',
        },
        modules: ['attestations'],
        roles: ['org_admin', 'attestations_user'],
        ui: {
            title: 'User attestations',
            subtitle: 'Manage and track user attestations',
            icon: UserCheck,
        },
    },
    validateSearch: zodValidator(UserAttestationSearchSchema),
    component: UserAttestations,
});

type QueryParams = RouterInputs['userAttestation']['listUserAttestations'];

const COLUMN_PERSIST_KEY = 'user-attestations-table-columns';

function UserAttestations() {
    const orgId = useOrgId();
    const navigate = useNavigate();
    const { can } = usePermissions();
    const { formatDate } = useFormatter();
    const searchParams = Route.useSearch();
    const canSendAttestations = can('user_attestation:create');

    const { config: filterConfig, ready: filterConfigReady } = useUserAttestationFilterConfig();

    const [input, setInput] = usePathState<QueryParams>('query-params', {
        limit: DEFAULT_PAGE_SIZE,
        orderBy: { sentAt: 'desc' },
        where: buildInitialWhereFromFilters(filterConfig, searchParams['user-attestation-filters']),
    });

    // Postpone the list query until saved views resolve which view loads first, so we fetch once
    // with the resolved where/sort/pageSize instead of unfiltered-then-refiltered (set by onResolved).
    const [viewsReady, setViewsReady] = useState(false);

    const { data, isLoading, refetch } = trpc.userAttestation.listUserAttestations.useQuery(input, {
        enabled: filterConfigReady && viewsReady,
    });

    const columns = useMemo(() => userAttestationColumns(formatDate), [formatDate]);

    const tableProps = usePrismaTable({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: !viewsReady || isLoading,
        columns,
        rowKey: (row) => String(row.id),
        queryParams: input,
        onParamsChange: setInput,
        pagination: {
            position: 'both',
        },
        columnVisibility: {
            enabled: true,
            persistKey: COLUMN_PERSIST_KEY,
            enableReordering: true,
        },
        refreshable: {
            enabled: true,
            onRefresh: () => refetch(),
        },
        showSorterTooltip: false,
    });

    const builtInViews = useEntityBuiltInViews('recipient');
    const views = useTableViews({
        entityType: 'USER_ATTESTATION',
        config: filterConfig,
        configReady: filterConfigReady,
        urlFilterKey: 'user-attestation-filters',
        builtInViews,
        initialFilters: searchParams['user-attestation-filters'],
        initialActiveViewId: searchParams.view,
        queryParams: input,
        setQueryParams: setInput,
        table: tableProps,
        columnPersistKey: COLUMN_PERSIST_KEY,
        defaultOrderBy: { sentAt: 'desc' },
        defaultPageSize: DEFAULT_PAGE_SIZE,
        onResolved: () => setViewsReady(true),
    });

    return (
        <PageController route={Route}>
            <TableViewsBar controller={views.controller} />

            <TableToolbar
                variant="enhanced"
                filters={
                    <FilterBar
                        config={filterConfig}
                        filters={views.filterState.filters}
                        whereClause={views.filterState.whereClause}
                        onAddFilter={views.filterState.addFilter}
                        onUpdateFilter={views.filterState.updateFilter}
                        onRemoveFilter={views.filterState.removeFilter}
                        onClearFilters={views.filterState.clearFilters}
                    />
                }
                actions={
                    canSendAttestations ? (
                        <Button.Add.Link
                            to="/console/$orgId/attestations/user-attestations/send"
                            params={{ orgId }}
                            variant="primary"
                            label="New user attestation"
                        />
                    ) : null
                }
            />

            <PrismaTable
                {...tableProps}
                onRow={({ id }) => ({
                    className: 'cursor-pointer',
                    onClick(e) {
                        navigate(
                            {
                                to: '/console/$orgId/attestations/user-attestations/$attestationId',
                                params: { orgId, attestationId: id },
                            },
                            e,
                        );
                    },
                })}
            />

            <Outlet />
        </PageController>
    );
}
